import { useState, useEffect, useCallback, useMemo } from 'react';
import apiService from '../services/api';

const LAUNCH_DATE_MS = new Date('2026-04-01T00:00:00Z').getTime();

export interface HealthData {
  mrr: number;
  arr: number;
  cashCollectedMtd: number;
  churnRate30d: number | null;
  totalActiveSubs: number;
  monthlyActive: number;
  annualActive: number;
  totalNewSinceCutoff: number;
  monthlySinceCutoff: number;
  annualSinceCutoff: number;
  newThisMonth: number;
  churned30d: number;
}

export interface AnomalySignal {
  type: 'positive' | 'warning' | 'alert' | 'info';
  metric: string;
  label: string;
  text: string;
  thisWeek: number | null;
  lastWeek: number | null;
  pctChange: number | null;
}

export interface TierBreakdown {
  planCode: string;
  displayName: string;
  displayTier: string;
  activeCount: number;
}

export interface GrowthKpis {
  newThisPeriod: number;
  netNew: number;
  mrrGrowth: number;
  annualRate: number;
}

export interface Costs {
  grokCostMTD: number;
  awsCostMTD: number;
  totalCostsMTD: number;
}

export interface IntelligenceData {
  health: HealthData;
  anomalySignals: AnomalySignal[];
  tierBreakdown: TierBreakdown[];
  growthKpis: GrowthKpis;
  trendData: {
    cumulative: { date: string; active: number }[];
    weekly: { week: string; monthlyNew: number; annualNew: number; totalNew: number }[];
    daily: { date: string; newSubs: number; churned: number }[];
  };
  costs: Costs;
  loading: boolean;
  refreshing: boolean;
  effectiveDays: string;
  refresh: () => void;
}

const DEFAULT_HEALTH: HealthData = {
  mrr: 0, arr: 0, cashCollectedMtd: 0, churnRate30d: null,
  totalActiveSubs: 0, monthlyActive: 0, annualActive: 0,
  totalNewSinceCutoff: 0, monthlySinceCutoff: 0, annualSinceCutoff: 0,
  newThisMonth: 0, churned30d: 0,
};

const DEFAULT_KPIS: GrowthKpis = { newThisPeriod: 0, netNew: 0, mrrGrowth: 0, annualRate: 0 };

type HookState = Omit<IntelligenceData, 'refresh' | 'effectiveDays'>;

const DEFAULT_STATE: HookState = {
  health: DEFAULT_HEALTH,
  anomalySignals: [],
  tierBreakdown: [],
  growthKpis: DEFAULT_KPIS,
  trendData: { cumulative: [], weekly: [], daily: [] },
  costs: { grokCostMTD: 0, awsCostMTD: 0, totalCostsMTD: 0 },
  loading: true,
  refreshing: false,
};

export function useIntelligenceData(timeRangeDays: string): IntelligenceData {
  const [state, setState] = useState<HookState>(DEFAULT_STATE);

  const effectiveDays = useMemo(() => {
    if (timeRangeDays === 'since-launch') {
      return String(Math.max(1, Math.ceil((Date.now() - LAUNCH_DATE_MS) / 86400000)));
    }
    return timeRangeDays;
  }, [timeRangeDays]);

  const loadData = useCallback(async (isRefresh = false) => {
    setState(prev => ({ ...prev, loading: !isRefresh, refreshing: isRefresh }));
    try {
      const [intelligenceRes, growthRes, grokSummary, awsCosts] = await Promise.all([
        apiService.get('/api/admin/subscriptions/intelligence'),
        apiService.get(`/api/admin/subscriptions/intelligence/growth-trends?days=${effectiveDays}`),
        (apiService as any).getGrokSummary('mtd').catch(() => null),
        (apiService as any).getAWSCostAnalytics().catch(() => null),
      ]);

      const intel = (intelligenceRes.data as any) ?? {};
      const growth = (growthRes.data as any) ?? {};

      const grokCostMTD = parseFloat(grokSummary?.summary?.total_grok_cost || '0');
      const awsCostMTD = (awsCosts as any)?.mtdAWSCosts?.rawValue || 0;

      setState({
        health: intel.health ?? DEFAULT_HEALTH,
        anomalySignals: intel.anomalySignals ?? [],
        tierBreakdown: intel.tierBreakdown ?? [],
        growthKpis: growth.kpis ?? DEFAULT_KPIS,
        trendData: {
          cumulative: growth.cumulative ?? [],
          weekly: growth.weekly ?? [],
          daily: growth.daily ?? [],
        },
        costs: { grokCostMTD, awsCostMTD, totalCostsMTD: grokCostMTD + awsCostMTD },
        loading: false,
        refreshing: false,
      });
    } catch (err) {
      console.error('[useIntelligenceData] fetch error:', err);
      setState(prev => ({ ...prev, loading: false, refreshing: false }));
    }
  }, [effectiveDays]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const refresh = useCallback(() => loadData(true), [loadData]);

  return { ...state, effectiveDays, refresh };
}
